/**
 * The main thread's half of the edge-detection worker: start one, ask it about
 * a frame, stop it.
 *
 * **It drops frames, on purpose.** A viewfinder produces sixty frames a second
 * and there is no use whatever for sixty answers a second — the guide is
 * redrawn a few times a second and a queue of stale frames waiting to be
 * analysed only makes the newest answer arrive later. So there is one request
 * in flight at a time and anything offered while the worker is busy is
 * answered `null` and forgotten. The next frame is a sixtieth of a second
 * away; none of them is worth waiting for.
 *
 * `startEdgeFinder` is what makes the worker exist, and the module that calls
 * it is dynamically imported, so the detector — this, the worker and
 * `receipt-edges.ts` — is a chunk nobody who does not open the camera fetches.
 */

import type { DetectRequest, DetectResponse } from '../workers/receipt-edges.worker'
import type { Frame, Quad } from './receipt-edges'

export type EdgeFinder = {
  /**
   * The quad in this frame, or `null` — which means either "nothing here looks
   * like paper" or "still busy with the last one". The caller treats both the
   * same way, by keeping the guide it already had.
   *
   * `frame.data`'s buffer is transferred and unusable afterwards.
   */
  find(frame: Frame): Promise<Quad | null>
  /** Takes the thread down. Anything still in flight resolves `null`. */
  close(): void
}

export function startEdgeFinder(): EdgeFinder {
  const worker = new Worker(new URL('../workers/receipt-edges.worker.ts', import.meta.url), {
    type: 'module',
  })

  let nextId = 1
  let waiting: { id: number; resolve: (quad: Quad | null) => void } | null = null
  let closed = false

  worker.onmessage = (event: MessageEvent<DetectResponse>) => {
    if (waiting === null || event.data.id !== waiting.id) return
    const settle = waiting.resolve
    waiting = null
    settle(event.data.quad)
  }

  // A worker that throws is a scanner without a guide, which is still a
  // scanner: the shutter goes on working and the capture is simply uncropped.
  worker.onerror = (event) => {
    console.warn('the edge detector stopped', event.message)
    if (waiting) {
      const settle = waiting.resolve
      waiting = null
      settle(null)
    }
  }

  return {
    find(frame) {
      if (closed || waiting !== null) return Promise.resolve(null)
      const id = nextId++
      const request: DetectRequest = {
        id,
        width: frame.width,
        height: frame.height,
        buffer: frame.data.buffer,
      }
      return new Promise<Quad | null>((resolve) => {
        waiting = { id, resolve }
        worker.postMessage(request, [request.buffer])
      })
    },

    close() {
      closed = true
      if (waiting) {
        const settle = waiting.resolve
        waiting = null
        settle(null)
      }
      worker.terminate()
    },
  }
}

/**
 * Whether this browser can run the scanner at all.
 *
 * Three things have to be true, and on a real phone they usually are: a camera
 * API, a worker to think on, and a secure context, because `getUserMedia` does
 * not exist on plain HTTP outside localhost. Where any of them is missing the
 * scanner is never offered and the capture input — which needs none of them —
 * is what somebody uses instead.
 */
export function scanningIsPossible(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof Worker !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  )
}
